import type { Project } from '../../api/types';
import { avatarTint } from '../../lib/format';
import { isResearchProject } from '../../projects/ProjectsProvider';

/**
 * One row of the project list — shared by the desktop rail and the mobile sheet so the two cannot
 * describe the same project differently.
 *
 * `project === null` is the "All projects" row. ⭐ It is a real, selectable option rather than a
 * "clear" affordance hidden somewhere else: "everything" is a legitimate thing to be looking at, and
 * it needs to be visibly SELECTED when it is, otherwise an unfiltered list reads as a broken filter.
 */
export function ProjectOption({
  project,
  selected,
  touch,
  onSelect,
}: {
  project: Project | null;
  selected: boolean;
  /** Mobile sheet sizing — 48px rows and larger type. Below 44px is not a touch target. */
  touch?: boolean;
  onSelect: () => void;
}) {
  const key = project?.key ?? 'All';
  const name = project?.name ?? 'All projects';
  const research = project ? isResearchProject(project) : false;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      title={project ? `${key} — ${name}` : name}
      className={`flex w-full items-center gap-2.5 rounded px-2 text-left ${
        touch ? 'min-h-12 py-2' : 'min-h-9 py-1.5'
      } ${
        selected
          ? 'bg-[color:var(--color-accent)]/12 text-[color:var(--color-accent)]'
          : 'text-[color:var(--color-ink)] hover:bg-black/5 dark:hover:bg-white/10'
      }`}
    >
      {/* ⭐ The KEY lives here and nowhere else in the row. It was in both the tile and the label
          first — "APFS · APFS Agent Portal" — which reads as noise at 240px and eats the width the
          name needs. The tile is the key, the label is the name, and neither repeats the other. */}
      <span
        aria-hidden="true"
        style={project ? { backgroundColor: avatarTint(project.key) } : undefined}
        className={`inline-flex shrink-0 items-center justify-center rounded font-bold text-white ${
          touch ? 'size-8' : 'size-7'
        } ${key.length > 3 ? 'text-[9px]' : 'text-[11px]'} ${
          project
            ? ''
            : 'border border-dashed border-[color:var(--color-ink-subtle)] !text-[color:var(--color-ink-subtle)]'
        }`}
      >
        {project ? key.slice(0, 4) : '∗'}
      </span>

      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        {/* The tile is decoration to a screen reader, so the key is spoken here instead — otherwise
            the only way to hear which project a row is would be to recognise its colour. */}
        {project ? <span className="sr-only">{key} — </span> : null}
        <span className="truncate text-sm font-medium">{name}</span>
        {/* ⚠️ Surfaced because it CHANGES THE VOCABULARY: a research project runs a 5-status set
            (Open · Researching · Blocked · Under Review · Concluded), not the 25 software ones.
            Without this tag, a status filter that suddenly matches nothing looks like a bug. */}
        {research ? (
          <span className="shrink-0 rounded bg-purple-100 px-1 text-[10px] font-bold text-purple-800 uppercase dark:bg-purple-900/60 dark:text-purple-200">
            research
          </span>
        ) : null}
      </span>
    </button>
  );
}
